import { useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { FormError } from '@/components/form/FormError';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { useSubmit } from '@/hooks/useSubmit';
import PasswordResetRequestModel from '@/models/services/PasswordResetRequestModel';

const schema = z.object({ email: z.email('Email invalide') });

type ForgotPasswordForm = z.infer<typeof schema>;

/** Mot de passe oublié : envoi d'un lien de réinitialisation (sert aussi à définir un premier mot de passe) */
export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { submit, submitting, error } = useSubmit();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({ resolver: zodResolver(schema) });

  const onSubmit = ({ email }: ForgotPasswordForm) =>
    submit(async () => {
      await new PasswordResetRequestModel().save({ email }, { silentError: true });
      setSentTo(email);
    });

  return (
    <AuthLayout title="Mot de passe oublié" subtitle="Recevez par e-mail un lien pour choisir un nouveau mot de passe">
      {sentTo ? (
        <div className="space-y-4">
          <p role="status" className="text-sm text-slate-700">
            Si un compte actif existe pour <strong>{sentTo}</strong>, un e-mail vient de lui être envoyé avec un lien
            valable 1 heure. Pensez à vérifier vos indésirables.
          </p>
          <BackToLogin />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <TextField
            id="email"
            label="Email professionnel"
            required
            type="email"
            autoComplete="username"
            placeholder="nom@entreprise.com"
            icon={Mail}
            error={errors.email?.message}
            {...register('email')}
          />

          <FormError error={error} />

          <Button type="submit" className="w-full" disabled={submitting}>
            <Send className="w-4 h-4" />
            Envoyer le lien
          </Button>
          <BackToLogin />
        </form>
      )}
    </AuthLayout>
  );
}

export function BackToLogin() {
  return (
    <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700">
      <ArrowLeft className="w-4 h-4" />
      Retour à la connexion
    </Link>
  );
}
